import { context, propagation } from '@opentelemetry/api'
import { env } from '@server/shared/constants/env'
import { HttpError } from '@server/shared/errors/http'
import { toCaseCompatible } from '@server/shared/utils/case-adapter'
import { preparePodoBackendRequest } from '@shared/apis/podo-backend-release'
import { getBrowserAuthSession } from '@shared/libs/browser-auth-session'
import { StatusCode } from 'hono/utils/http-status'

import type { Params } from '../types'
import { isTrustedBackendBearerRejection } from './backend-bearer-rejection'

const PODO_API_TIMEOUT_MS = 120_000

export type PodoFetcherResponse<R> = {
  resultCd: number
  resultCdName: string
  message: string | null
  data: R
}

export type PodoCompatibleErrorCause<T = unknown> = {
  resultCd: number
  resultCdName: string
  message: string | null
  resultMsg: string | null
  data: T | null
  result: T | null
  rawData?: unknown
  raw_data?: unknown
}

export type LegacyPodoErrorResponse<T = unknown> = {
  resultCd: string
  resultMsg?: string
  result: T | null
}

type PodoFetcherPayload<R> = R extends { resultCd: unknown; data: infer Data }
  ? Data
  : R extends { resultCd: unknown; result: infer Result }
    ? Result
    : R

type PodoFetcherOptions = {
  responseType?: 'normalized' | 'raw'
  /** Optional private metadata must not initiate auth recovery or navigation. Defaults to true. */
  recoverBrowserSession?: boolean
  httpErrorMode?: 'base' | 'legacy'
  throwOnErrorResult?: boolean
  signal?: AbortSignal
  /** Total request budget, including session recovery and response body consumption. */
  timeoutMs?: number
  /** Owned credential cleanup may prohibit redirects; callers cannot enable redirect following. */
  redirect?: 'error'
  /** Auth callers must distinguish an actual HTTP rejection from a conflicting business envelope. */
  preserveHttpErrorStatus?: boolean
}

/**
 * Injects W3C trace context (traceparent, tracestate) headers into the given headers object.
 * If OTEL SDK is not initialized, this is a safe no-op.
 */
const injectTraceContext = (headers: Record<string, string>): Record<string, string> => {
  const carrier: Record<string, string> = { ...headers }
  propagation.inject(context.active(), carrier)
  return carrier
}

const fetchWithTimeout = async <T>(
  input: URL,
  init: RequestInit,
  timeoutMs: number,
  url: string,
  readResponse: (response: Response) => Promise<T>,
  recoverBrowserSession = true,
): Promise<T> => {
  const controller = new AbortController()
  const timeoutError = () => new HttpError(504, { code: 'PODO_API_TIMEOUT', message: 'PODO_API_TIMEOUT' })
  let hasResponse = false
  let timedOut = false
  const abortError = () => (hasResponse && !timedOut ? new DOMException('Aborted', 'AbortError') : timeoutError())
  let rejectAborted!: (error: Error) => void
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAborted = reject
  })
  const onAbort = () => rejectAborted(abortError())
  controller.signal.addEventListener('abort', onAbort, { once: true })
  const forwardAbort = () => controller.abort()
  init.signal?.addEventListener('abort', forwardAbort, { once: true })
  const timeoutId = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  try {
    const request = async () => {
      if (init.signal?.aborted) {
        controller.abort()
      }
      const requestInit = { ...init, signal: controller.signal }
      const backendOrigin = new URL(env.NEXT_PUBLIC_PODO_API_URL).origin
      const isAuthenticatedBrowserRequest =
        typeof window !== 'undefined' &&
        input.origin === backendOrigin &&
        /^Bearer\s+\S+$/i.test(new Headers(init.headers).get('Authorization') ?? '') &&
        !/^\/api\/v\d+\/(authentication|oauth|auth)(\/|$)/.test(input.pathname)
      const session = isAuthenticatedBrowserRequest && recoverBrowserSession ? getBrowserAuthSession() : null
      let response: Response
      try {
        response = session
          ? await session.fetch(new Request(input, requestInit), { backendOrigin })
          : await fetch(input, requestInit)
      } catch (error) {
        if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
          throw timeoutError()
        }
        throw new HttpError(503, {
          code: 'PODO_API_NETWORK_ERROR',
          message: 'PODO_API_NETWORK_ERROR',
          cause: { url, error },
        })
      }
      hasResponse = true
      if (controller.signal.aborted) {
        throw abortError()
      }
      return readResponse(response)
    }
    // A caller deadline must not cancel another request's shared authentication recovery.
    // Racing bounds this caller even while that recovery drains its cookie writes.
    return await Promise.race([request(), aborted])
  } catch (error) {
    if (controller.signal.aborted) {
      throw abortError()
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
    init.signal?.removeEventListener('abort', forwardAbort)
    controller.signal.removeEventListener('abort', onAbort)
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const hasOwn = <K extends PropertyKey>(value: object, key: K): value is object & Record<K, unknown> =>
  Object.prototype.hasOwnProperty.call(value, key)

const toResultCode = (resultCd: unknown): number => {
  const code = Number(resultCd)
  return Number.isNaN(code) ? 0 : code
}

const toHttpStatusCode = (resultCd: unknown, fallbackStatus: number): StatusCode => {
  const code = toResultCode(resultCd)
  const statusCode = code >= 400 && code <= 599 ? code : fallbackStatus

  return (statusCode >= 400 && statusCode <= 599 ? statusCode : 500) as StatusCode
}

const toNullableMessage = (message: unknown): string | null => {
  if (message === null || message === undefined) {
    return null
  }

  return typeof message === 'string' ? message : String(message)
}

const toResultCdName = (resultCdName: unknown, resultCd: number): string => {
  if (typeof resultCdName === 'string' && resultCdName.length > 0) {
    return resultCdName
  }

  return resultCd >= 400 ? 'INTERNAL_PODO_API_SERVER_ERROR' : 'OK'
}

const normalizePodoResponse = <R>(result: unknown): PodoFetcherResponse<R> => {
  if (!isRecord(result)) {
    throw new HttpError(500, {
      code: 'INTERNAL_PODO_API_SERVER_ERROR',
      message: 'INVALID_PODO_API_RESPONSE',
      cause: result,
    })
  }

  const resultCd = toResultCode(result.resultCd)
  const resultCdName = toResultCdName(result.resultCdName, resultCd)

  if ('data' in result) {
    return {
      resultCd,
      resultCdName,
      message: toNullableMessage(result.message),
      data: result.data as R,
    }
  }

  if ('result' in result) {
    return {
      resultCd,
      resultCdName,
      message: toNullableMessage(result.message ?? result.resultMsg),
      data: result.result as R,
    }
  }

  if ('resultCd' in result || 'resultCdName' in result || 'resultMsg' in result || 'message' in result) {
    return {
      resultCd,
      resultCdName,
      message: toNullableMessage(result.message ?? result.resultMsg),
      data: result as R,
    }
  }

  throw new HttpError(500, {
    code: 'INTERNAL_PODO_API_SERVER_ERROR',
    message: 'INVALID_PODO_API_RESPONSE',
    cause: result,
  })
}

const getErrorPayload = (result: Record<string, unknown>): unknown => {
  if (hasOwn(result, 'data')) {
    return result.data
  }

  if (hasOwn(result, 'result')) {
    return result.result
  }

  return null
}

const normalizePodoErrorResponse = <T>(
  result: unknown,
  fallbackResultCd: number,
  fallbackMessage?: string,
): PodoCompatibleErrorCause<T> => {
  if (!isRecord(result)) {
    const message = toNullableMessage(result ?? fallbackMessage)

    return toCaseCompatible({
      resultCd: toHttpStatusCode(fallbackResultCd, 500),
      resultCdName: 'INTERNAL_PODO_API_SERVER_ERROR',
      message,
      resultMsg: message,
      data: result as T,
      result: result as T,
    })
  }

  const resultCd = toResultCode(result.resultCd) || fallbackResultCd
  const resultCdName = toResultCdName(result.resultCdName, resultCd)
  const payload = getErrorPayload(result)
  const message =
    toNullableMessage(result.message ?? result.resultMsg) ??
    (typeof payload === 'string' ? payload : null) ??
    fallbackMessage ??
    null
  const resultMsg = toNullableMessage(result.resultMsg ?? result.message) ?? message

  return toCaseCompatible({
    resultCd,
    resultCdName,
    message,
    resultMsg,
    data: payload as T | null,
    result: payload as T | null,
  })
}

export const getPodoErrorCause = <T = unknown>(error: unknown): PodoCompatibleErrorCause<T> | null => {
  if (!isRecord(error) || !isRecord(error.cause)) {
    return null
  }

  const cause = toCaseCompatible<Record<string, unknown>>(error.cause)
  const resultCd = toResultCode(cause.resultCd)

  if (resultCd === 0) {
    return null
  }

  const payload = hasOwn(cause, 'result') ? cause.result : hasOwn(cause, 'data') ? cause.data : null
  const message =
    toNullableMessage(cause.message ?? cause.resultMsg) ?? (typeof payload === 'string' ? payload : null) ?? null
  const resultMsg = toNullableMessage(cause.resultMsg ?? cause.message) ?? message

  return toCaseCompatible({
    ...cause,
    resultCd,
    resultCdName: toResultCdName(cause.resultCdName, resultCd),
    message,
    resultMsg,
    data: payload as T | null,
    result: payload as T | null,
  })
}

export const toLegacyPodoErrorResponse = <T = unknown>(
  error: unknown,
  options: { fallbackResultToMessage?: boolean } = {},
): LegacyPodoErrorResponse<T> | null => {
  const cause = getPodoErrorCause<T>(error)

  if (!cause) {
    return null
  }

  const resultMsg =
    typeof cause.resultMsg === 'string'
      ? cause.resultMsg
      : typeof cause.message === 'string'
        ? cause.message
        : undefined
  const result =
    (cause.result === null || cause.result === undefined) && options.fallbackResultToMessage
      ? ((resultMsg ?? null) as T | null)
      : cause.result

  return {
    resultCd: String(cause.resultCd),
    ...(resultMsg !== undefined ? { resultMsg } : {}),
    result,
  }
}

const throwLegacyHttpError = async (response: Response, params: Params): Promise<never> => {
  const { body } = params
  let result: unknown

  try {
    result = toCaseCompatible(await response.json())
  } catch {
    const text = await response.text().catch(() => '')
    const cause = normalizePodoErrorResponse(text, response.status, text)
    throw new HttpError(response.status as StatusCode, {
      code: 'INTERNAL_PODO_API_SERVER_ERROR',
      message: 'PODO_API_REQUEST_FAILED',
      cause: toCaseCompatible({ ...cause, response, body, rawData: text, data: text }),
    })
  }

  const cause = normalizePodoErrorResponse(result, response.status)

  throw new HttpError(500, {
    code: 'INTERNAL_PODO_API_SERVER_ERROR',
    message: 'PODO_API_REQUEST_FAILED',
    cause: toCaseCompatible({ ...cause, response, body, rawData: result, data: result }),
  })
}

const throwBaseHttpError = async <Cause>(
  response: Response,
  _params: Params,
  preserveHttpErrorStatus = false,
): Promise<never> => {
  let result: unknown

  try {
    result = toCaseCompatible(await response.json())
  } catch {
    const text = await response.text().catch(() => '')
    const cause = normalizePodoErrorResponse(text, response.status, text)

    throw new HttpError<Cause>(response.status as StatusCode, {
      message: 'PODO_API_REQUEST_FAILED',
      code: 'INTERNAL_PODO_API_SERVER_ERROR',
      cause: toCaseCompatible({ ...cause, rawData: text }) as Cause,
    })
  }

  const cause = normalizePodoErrorResponse(result, response.status)

  throw new HttpError<Cause>(
    preserveHttpErrorStatus ? (response.status as StatusCode) : toHttpStatusCode(cause.resultCd, response.status),
    {
      message: 'PODO_API_REQUEST_FAILED',
      code: cause.resultCdName,
      cause: toCaseCompatible({ ...cause, rawData: result }) as Cause,
    },
  )
}

const fetchPodoJson = async <Cause>(
  params: Params,
  httpErrorMode: PodoFetcherOptions['httpErrorMode'],
  signal?: AbortSignal,
  preserveHttpErrorStatus = false,
  redirect?: 'error',
  timeoutMs = PODO_API_TIMEOUT_MS,
  recoverBrowserSession = true,
) => {
  const { method, body, header, url } = params
  const request = preparePodoBackendRequest({
    url,
    method,
    publicBackendUrl: env.NEXT_PUBLIC_PODO_API_URL,
    headers: { 'Content-Type': 'application/json', ...header },
    server:
      typeof window === 'undefined' ? { backendUrl: env.PODO_API_URL, release: env.PODO_BACKEND_RELEASE } : undefined,
  })

  return fetchWithTimeout(
    request.url,
    {
      method,
      headers: injectTraceContext(request.headers),
      ...(request.redirect || redirect ? { redirect: request.redirect ?? redirect } : {}),
      body: JSON.stringify(body),
      signal,
    },
    Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : PODO_API_TIMEOUT_MS,
    url,
    async (response) => {
      if (!response.ok) {
        if (
          httpErrorMode !== 'legacy' &&
          preserveHttpErrorStatus &&
          isTrustedBackendBearerRejection(response, params, request.url)
        ) {
          // The challenge/body may contain sensitive details; keep only the trusted classification.
          throw new HttpError(401, {
            code: 'PODO_BACKEND_BEARER_INVALID_TOKEN',
            message: 'PODO_API_REQUEST_FAILED',
            authenticationFailure: 'BEARER_INVALID_TOKEN',
          })
        }
        if (httpErrorMode === 'legacy') {
          await throwLegacyHttpError(response, params)
        }

        await throwBaseHttpError<Cause>(response, params, preserveHttpErrorStatus)
      }

      return toCaseCompatible(await response.json())
    },
    recoverBrowserSession,
  )
}

export function podoFetcher<R>(params: Params, options: PodoFetcherOptions & { responseType: 'raw' }): Promise<R>
export function podoFetcher<R>(
  params: Params,
  options?: PodoFetcherOptions & { responseType?: 'normalized' },
): Promise<PodoFetcherResponse<PodoFetcherPayload<R>>>
export async function podoFetcher<R, Cause = unknown>(
  params: Params,
  options: PodoFetcherOptions = {},
): Promise<PodoFetcherResponse<PodoFetcherPayload<R>> | R> {
  const result = await fetchPodoJson<Cause>(
    params,
    options.httpErrorMode ?? 'base',
    options.signal,
    options.preserveHttpErrorStatus,
    options.redirect,
    options.timeoutMs,
    options.recoverBrowserSession,
  )

  if (options.responseType === 'raw') {
    return result as R
  }

  const normalizedResult = normalizePodoResponse<PodoFetcherPayload<R>>(result)

  if (options.throwOnErrorResult && normalizedResult.resultCd >= 400) {
    const cause = normalizePodoErrorResponse(result, normalizedResult.resultCd, normalizedResult.message ?? undefined)

    // A failed envelope on HTTP 2xx cannot establish transport-level credential rejection.
    throw new HttpError(options.preserveHttpErrorStatus ? 503 : toHttpStatusCode(cause.resultCd, 500), {
      code: cause.resultCdName,
      message: 'PODO_API_REQUEST_FAILED',
      cause: toCaseCompatible({ ...cause, rawData: result }),
    })
  }

  return normalizedResult
}
